import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Layers, ScrollText } from 'lucide-react';
import type { PrototypeDeployStatus } from '@aiai/shared';
import { fetchPrototypeStats } from '../api';
import { qk } from '../api/queryKeys';
import { formatRelative } from '../lib/format';
import { StatusBadge } from './StatusBadge';

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function Chip({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface-2 px-2.5 py-1 text-xs font-medium text-ink">
      <span className="text-[10px] font-bold uppercase tracking-wide text-ink-muted">{label}</span>
      <span className="font-mono">{children}</span>
    </span>
  );
}

/**
 * The prototype's vitals strip: the deploy status word (owns it — the header no
 * longer carries a badge), plus CPU / RAM / uptime chips and the logs/inspect
 * toggles when the container is live, stopped or sleeping (idle sleep: the
 * strip then says when the last visit was). Polls the read-only stats
 * endpoint only while live; a failed poll degrades to "—" (never errors the
 * page). When not live the strip is just the status pill — while a deploy is
 * in flight (provisioning/building) and `onToggleBuild` is given, that pill is
 * the toggle for the collapsed-by-default build progress card.
 */
export function PrototypeStatsBar({
  prototypeId,
  deployStatus,
  logsOpen,
  onToggleLogs,
  inspectOpen,
  onToggleInspect,
  buildOpen = false,
  onToggleBuild,
  lastRequestAt = null,
}: {
  prototypeId: string;
  deployStatus: PrototypeDeployStatus;
  /** Last Traefik-counted request; shown while the prototype is asleep. */
  lastRequestAt?: string | null;
  logsOpen: boolean;
  onToggleLogs: () => void;
  inspectOpen: boolean;
  onToggleInspect: () => void;
  buildOpen?: boolean;
  onToggleBuild?: () => void;
}) {
  const live = deployStatus === 'live';
  const sleeping = deployStatus === 'sleeping';
  const canViewLogs = live || sleeping || deployStatus === 'stopped' || deployStatus === 'failed';
  const canInspect = live || sleeping || deployStatus === 'stopped';
  const canToggleBuild =
    !!onToggleBuild && (deployStatus === 'provisioning' || deployStatus === 'building');

  const statsQuery = useQuery({
    queryKey: qk.prototypeStats(prototypeId),
    queryFn: () => fetchPrototypeStats(prototypeId),
    enabled: live,
    refetchInterval: live ? 4000 : false,
    staleTime: 4000,
  });

  const s = statsQuery.data;
  const dash = '—';

  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-dashed border-line pt-4">
      {/* The strip now owns the status word — the header dropped its badge. */}
      {canToggleBuild ? (
        <button
          type="button"
          className="inline-flex items-center gap-0.5 rounded-full"
          onClick={onToggleBuild}
          aria-expanded={buildOpen}
          aria-controls="prototype-build-panel"
          aria-label={buildOpen ? 'Hide build progress' : 'Show build progress'}
          title={buildOpen ? 'Hide build progress' : 'Show build progress'}
        >
          <StatusBadge status={deployStatus} />
          {buildOpen ? (
            <ChevronDown className="h-3.5 w-3.5 text-ink-muted" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-ink-muted" aria-hidden="true" />
          )}
        </button>
      ) : (
        <StatusBadge status={deployStatus} />
      )}
      {sleeping && <Chip label="Last visit">{formatRelative(lastRequestAt)}</Chip>}
      {live && (
        <>
          <Chip label="CPU">{s ? `${s.cpuPercent}%` : dash}</Chip>
          <Chip label="RAM">{s ? `${s.memUsedMb} / ${s.memLimitMb} MB` : dash}</Chip>
          <Chip label="Up">{s ? formatUptime(s.uptimeSeconds) : dash}</Chip>
        </>
      )}
      {/* Only shown when there's something wrong: a container that keeps
          restarting (or was OOM-killed) is thrashing under its RAM cap — the
          signal-orange styling flags it as needing attention. */}
      {live && s && (s.restartCount > 0 || s.oomKilled) && (
        <span
          className="inline-flex items-center gap-1.5 rounded-full border border-signal bg-signal/10 px-2.5 py-1 text-xs font-medium text-signal"
          title={
            s.oomKilled
              ? 'The container was killed for exceeding its memory cap and restarted.'
              : 'The container has restarted since its last deploy.'
          }
        >
          <span className="text-[10px] font-bold uppercase tracking-wide">
            {s.oomKilled ? 'OOM' : 'Restarts'}
          </span>
          <span className="font-mono">{s.restartCount}</span>
        </span>
      )}
      <div className="ml-auto flex items-center gap-2">
        {canViewLogs && (
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-full border border-ink px-3 py-1 text-xs font-semibold text-ink hover:bg-ink hover:text-white transition-colors"
            onClick={onToggleLogs}
            aria-expanded={logsOpen}
            aria-controls="prototype-logs-panel"
          >
            <ScrollText className="h-3.5 w-3.5" aria-hidden="true" />
            {logsOpen
              ? live
                ? 'Hide logs'
                : 'Hide last logs'
              : live
                ? 'View logs'
                : 'View last logs'}
          </button>
        )}
        {canInspect && (
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-full border border-ink px-3 py-1 text-xs font-semibold text-ink hover:bg-ink hover:text-white transition-colors"
            onClick={onToggleInspect}
            aria-expanded={inspectOpen}
            aria-controls="prototype-inspect-panel"
          >
            <Layers className="h-3.5 w-3.5" aria-hidden="true" />
            {inspectOpen ? 'Hide details' : 'Container details'}
          </button>
        )}
      </div>
    </div>
  );
}
